import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { BrowserRouter, Redirect, Route, Switch } from 'react-router-dom';
import qs from 'qs';
import ROLES from 'constants/UserRoles';
import LandingModule from 'components/modules/landing/LandingModule';
import Booking from 'components/modules/booking/v2/Booking';
import Login from 'components/session/login';
import CustomerDetails from 'components/modules/user/customer-details';
import AccountAccessVerification from 'components/modules/user/account-access-verification';
import ShipmentDetailPage from 'components/modules/shipments/ShipmentDetailsFullPage';
import ReferenceNumbersMasterList from 'components/modules/tools/ReferenceNumbersMasterList';
import DoRequestChanges from 'components/modules/shipments/delivery-order/RequestChanges';
import ShipmentExceptions from 'components/modules/exceptions/ShipmentExceptions';
import OrderExceptions from 'components/modules/exceptions/OrderExceptions';
import { AccessorialExceptionsRouter as AccessorialExceptions } from 'components/modules/exceptions/AccessorialExceptionsRouter';
import InvoiceModule from 'components/modules/accounting/invoices/invoice-module';
import InvoiceDetails from 'components/modules/accounting/invoices/invoice-details';
import PayoutModule from 'components/modules/accounting/payouts/payout-module';
import PayoutDetails from 'components/modules/accounting/payouts/payout-details';
import SettingsModule from 'components/modules/settings/settings-module';
import GrowthReport from 'components/modules/reporting/GrowthReport';
import DailyReport from 'components/modules/reporting/DailyReport';
import { Tariffs } from 'components/modules/tools/v2/Tariffs';
import FleetView from 'components/modules/fleet/FleetView';
import PasswordReset from 'components/modules/selfcare/PasswordReset';
import ShipperBillingForm from 'components/modules/user/shipper/billing/MainForm';
import ShipmentListModule from 'components/modules/shipments/list/ShipmentListModule';
import PublicShipmentTracking from 'components/modules/public/tracking';
import SessionStore from 'stores/SessionStore';
import SupportRequestVerification from 'components/site/support-request-verification';
import LaneRateResponse from 'components/site/lane-rate-response';
import LaneRatesRC from 'components/modules/pricing/rate-conditions/lane-rates/LaneRatesRateConditions';
import AccessorialRatesRateConditions from 'components/modules/pricing/rate-conditions/accessorial-rates/AccessorialRatesRateConditions';
import AccessorialRateResponse from 'components/site/accessorial-rate-response';
import ToolsPage from 'components/modules/ToolsPage';
import { AuthorizationWrapper } from 'components/wrappers/AuthorizationWrapper';
import { AuthContext } from 'providers/authorization-context';
import { isProd } from 'helpers/helper_functions';
import AccessorialRequestsPage from 'components/modules/user/shipper/AccessorialRequestsPage';
import StopDocumentRequirementsPage from 'components/modules/user/shipper/StopDocumentRequirementsPage';
import ReferenceNumbersPage from 'components/modules/user/shipper/ReferenceNumbersPage';
import USER_TYPES from 'constants/UserTypes';
import WarehouseUserProfile from 'components/modules/user/warehouse/Profile';
import EmptyNotify from 'components/modules/user/warehouse/EmptyNotify';
import CacheBusting from './CacheBusting';
import CarrierDocuments from './modules/carrier/Documents';
import CarrierDashboard from './modules/carrier/Dashboard';
import BulkAuditTool from './modules/carrier/BulkAuditTool';
import CarrierBilling from './modules/carrier/Billing';
import NotificationsPage from './modules/carrier/NotificationPage';
import UserNotificationsPage from './modules/user/shipper/NotificationPage';
import CarrierPayoutModule from './modules/accounting/payouts/carrier/payout-module';
import CarrierPayoutDetails from './modules/accounting/payouts/carrier/payout-details';
import DriverMap from './modules/drivers/DriverMap';
import DriverList from './modules/drivers/DriverList';
import DriverEditForm from './modules/drivers/DriverEditForm';
import ShipmentTracking from './modules/shipments/ShipmentTracking';
import Equipment from './modules/equipment/List';
import LocationIndex from './modules/locations';
import LocationModule from './modules/locations/Module';
import QuotesList from './modules/quotes/List';
import DrayageQuotePage from './modules/quotes/DrayageQuotePage';
import MarketsIndex from './modules/markets';
import CustomerList from './modules/customers/Customers';
import OrderDetails from './modules/orders/Module';
import OrderList from './modules/orders/List';
import ExploreUnifiedView from './modules/master/UnifiedViewFlagWrapper';
import ExploreOrdersUnifiedView from './modules/master/orders/Explore';
import { Dashboard as UnifiedViewDashboard } from './modules/master/Dashboard';
import Reports from './modules/master/Reports';
import UVColumnConfig from './modules/master/UVColumnConfig';
import AccessorialModule from './modules/shipper/AccessorialModule';
import Zones from './modules/tools/Zones';
import AccessorialManagementCenter from './modules/tools/AccessorialManagementCenter';
import MarketplaceConfig from './modules/marketplace-config/Markup';
import ReasonCodes from './modules/marketplace-config/ReasonCodes';
import LoadBoard from './offers/LoadBoard';
import DeveloperDetails from './modules/user/shipper/developer-details';
import { AccessorialRates } from './modules/pricing/v2/AccessorialRates';
import AppointmentModule from './modules/appointments/AppointmentModule';
import AppointmentDetails from './modules/appointments/AppointmentDetails';
import BulkShipmentCreation from './modules/shipments/BulkShipmentUpload';
import OfferPlanner from './modules/offer-planner/OfferPlanner';
import EmptyReturnInstructions from './modules/public/emptyInstructions';
import YardManagement from './modules/user/YardManagement';
import IntegrationSettings from './modules/user/shipper/IntegrationSettings';
import CarrierIntegrationSettings from './modules/user/carrier/CarrierIntegrationSettings';
import BillingV2 from './modules/user/shipper/billingv2';
import Team from './modules/user/shipper/team';
import AccountActivity from './modules/user/AccountActivity';
import YardsList from './modules/yards/YardsList';
import YardDetails from './modules/yards/YardDetails';
import MarketRates from './modules/yards/MarketRates';
import DriversSettings from './modules/user/carrier/DriversSettings';
import NotificationsSettings from './modules/user/carrier/NotificationsSettings';
import ActivitySettings from './modules/user/carrier/ActivitySettings';
import BillingSettings from './modules/user/carrier/BillingSettings';
import QuickPay from './modules/user/carrier/quickpay/QuickPay';
import DrayageFscSettings from './modules/user/rates/drayage/FscSettings';
import ShorthaulFscSettings from './modules/user/rates/shorthaul/FscSettings';
import LaneRatesRateCondtionsEditPage from './modules/pricing/rate-conditions/lane-rates/LaneRatesEditPage';
import { LaneRatesViewPage } from './modules/pricing/rate-conditions/lane-rates/LaneRatesViewPage';
import EmptyNotifyRequests from './modules/user/warehouse/EmptyNotifyRequests';
import ManageWarehouse from './modules/warehouse/ManageWarehouse';
import ErrorBoundary from './ErrorBoundary';
import TestErrorPage from './TestErrorPage';
import ManageQuickPayDiscountTerms from './modules/quickpayments/ManageQuickPayDiscountTerms';
import { PageProvider } from './modules/master/PageContext';
import CarrierTeam from './modules/user/carrier/team';
import RateCard from './v2/RateCard';

function routerErrorHandler(error) {
  console.error('Error occurred while routing');
  console.error(error.message);
  console.error(error.stack);
}

function getCarrierDashboardComponent() {
  if (SessionStore.isShipper()) {
    return null;
  }
  return CarrierDashboard;
}

function getBillingComponent() {
  if (SessionStore.isShipper()) {
    return ShipperBillingForm;
  }

  if (SessionStore.isCarrier()) {
    return CarrierBilling;
  }
  return null;
}

function getAccessorialRequestsPage() {
  if (SessionStore.isAdmin()) {
    return <AccessorialRequestsPage />;
  }
  return <Redirect to="/" />;
}

function getStopDocumentRequirementsPage() {
  if (SessionStore.isAdmin()) {
    return <StopDocumentRequirementsPage />;
  }
  return <Redirect to="/" />;
}

function getReferenceNumberPage() {
  if (SessionStore.isAdmin()) {
    return <ReferenceNumbersPage />;
  }
  return <Redirect to="/" />;
}

function getShipmentDetailComponent() {
  if (SessionStore.isAdmin()) {
    return ShipmentDetailPage;
  }
  return ShipmentTracking;
}

function getQueryString(path = '') {
  const query = path.split('?');
  if (!query) {
    return null;
  }
  return qs.parse(query[query.length - 1], { ignoreQueryPrefix: true });
}

function checkRedirectPath(query = {}, path = '') {
  return !query.action || !/request-changes/.test(path);
}

const RedirectToHome = () => {
  return <Redirect to="/" />;
};

function getLandingPage() {
  const user = SessionStore.getUser();

  if (user._type === 'Carrier' && !SessionStore.isThirdPartyCarrier(user)) {
    return getCarrierDashboardComponent();
  }
  return LandingModule;
}

// function getLaneRates() {
//   if (SessionStore.isAdmin()) {
//     return <LaneRates />;
//   }
//   return <Redirect to="/" />;
// }

// function getLaneRatesEdit() {
//   if (SessionStore.isAdmin()) {
//     return <LaneRatesEditPage />;
//   }
//   return <Redirect to="/" />;
// }

function getLaneRatesRateConditionsEdit({ match, location }) {
  if (
    SessionStore.isAdmin() &&
    SessionStore.isAbleToEditAccessorialLaneRate()
  ) {
    return <LaneRatesRateCondtionsEditPage />;
  }
  // Redirect to view mode if user lacks edit permission
  if (match.params.id) {
    return <Redirect to={`/lane-rates/${match.params.id}`} />;
  }
  if (location.search) {
    return <Redirect to={`/lane-rates/view${location.search}`} />;
  }
  return <Redirect to="/lane-rates" />;
}

function getAccessorialRates() {
  if (SessionStore.isAdmin()) {
    return <AccessorialRates />;
  }
  return <Redirect to="/" />;
}

function getOfferPlanner() {
  if (SessionStore.isAdmin()) {
    return <OfferPlanner />;
  }
  return <Redirect to="/" />;
}
function renderWarehouseRoutes(redirect) {
  const status = SessionStore.getShipperWarehouseRelationStatus();
  const isShipperRelationEnabled = status === 'true';
  if (redirect) {
    return <Redirect to={redirect} />;
  }
  return (
    <Switch>
      <Route exact path="/" component={EmptyNotify} />
      <Route
        exact
        path="/warehouse/user/profile"
        component={WarehouseUserProfile}
      />

      {isShipperRelationEnabled && (
        <Route
          exact
          path="/warehouse/empty-notify-requests/:reportId"
          component={EmptyNotifyRequests}
        />
      )}
      <Route path="*" component={RedirectToHome} />
    </Switch>
  );
}

function renderPrivate(redirect) {
  return (
    <div className="">
      {redirect && <Redirect to={redirect} />}
      <Route exact path="/" component={getLandingPage()} />
      <Route
        exact
        name="test-error-page"
        path="/test-error"
        component={TestErrorPage}
      />
      {!SessionStore.isMessageOnlyUser() && (
        <div>
          <div className="">
            <Route exact name="fleet" path="/fleet" component={FleetView} />
            <Route
              exact
              name="quotes"
              path="/quotes/:id?"
              component={QuotesList}
            />
            <Route
              exact
              name="drayage-quotes"
              path="/drayage-quotes"
              component={DrayageQuotePage}
            />
            <Route
              exact
              name="booking"
              path="/booking/:id?"
              component={Booking}
            />
            <Route
              exact
              name="landing"
              path="/landing"
              component={LandingModule}
            />
            <Route
              exact
              name="shipment"
              path="/shipments/:id"
              component={getShipmentDetailComponent()}
            />
            <Route
              exact
              name="shipment-request-changes"
              path="/shipments/:id/request-changes"
              component={DoRequestChanges}
            />
            <Route
              exact
              name="shipments"
              path="/shipments"
              component={ShipmentListModule}
            />
            <Route
              exact
              name="exceptions/shipments"
              path="/exceptions/shipments"
              component={ShipmentExceptions}
            />
            <Route
              exact
              name="exceptions/orders"
              path="/exceptions/orders"
              component={OrderExceptions}
            />
            <Route
              exact
              name="exceptions/accessorials"
              path="/exceptions/accessorials"
              component={AccessorialExceptions}
            />
            <Route
              exact
              name="invoices"
              path="/invoices"
              component={InvoiceModule}
            />
            <Route
              exact
              name="accessorials"
              path="/accessorials"
              component={AccessorialModule}
            />
            <Route
              exact
              name="invoice"
              path="/invoices/:id"
              component={InvoiceDetails}
            />
            <Route
              exact
              name="payouts"
              path="/payouts"
              component={PayoutModule}
            />
            <Route
              exact
              name="carrier-payouts"
              path="/carrier-payouts"
              component={CarrierPayoutModule}
            />
            <Route
              exact
              name="payout"
              path="/payouts/:id"
              component={PayoutDetails}
            />
            <Route
              exact
              name="reporting"
              path="/reporting"
              component={GrowthReport}
            />
            <Route
              exact
              name="reporting/growth"
              path="/reporting/growth"
              component={GrowthReport}
            />
            <Route
              exact
              name="reporting/daily"
              path="/reporting/daily"
              component={DailyReport}
            />
            <Route
              exact
              name="tariffs"
              path="/tools/tariffs"
              component={Tariffs}
            />
            <Route
              exact
              name="settings"
              path="/settings"
              component={SettingsModule}
            />
            <AuthorizationWrapper
              excludedUserTypes={[USER_TYPES.CARRIER, USER_TYPES.SHIPPER]}
            >
              <Route
                exact
                name="users"
                path="/users"
                component={SettingsModule}
              />
              <Route
                exact
                name="user"
                path="/users/:id"
                component={CustomerDetails}
              />
              <Route
                exact
                name="warehouse"
                path="/manage/warehouse"
                component={ManageWarehouse}
              />
              <Route
                exact
                name="quick_pay"
                path="/pricing/quick-pay"
                component={ManageQuickPayDiscountTerms}
              />
              <Route
                exact
                name="yard-settings"
                path="/users/:id/settings/yard"
                component={YardManagement}
              />
              <Route
                exact
                name="notification-settings"
                path="/users/:id/settings/notifications"
                component={UserNotificationsPage}
              />
              <Route
                exact
                name="team"
                path="/users/:id/settings/team"
                component={Team}
              />
              <Route
                exact
                name="billing"
                path="/users/:id/settings/billing"
                component={BillingV2}
              />
              <Route
                exact
                name="accessorials"
                path="/users/:id/settings/accessorial-requests"
                component={getAccessorialRequestsPage}
              />
              <Route
                exact
                name="stop-document-requirements-settings"
                path="/users/:id/settings/stop-document-requirements"
                component={getStopDocumentRequirementsPage}
              />
              <Route
                exact
                name="reference-numbers-settings"
                path="/users/:id/settings/reference-numbers"
                component={getReferenceNumberPage}
              />
              <Route
                exact
                name="integration-settings"
                path="/users/:id/settings/integrations"
                component={IntegrationSettings}
              />
              <Route
                exact
                name="account-activity"
                path="/users/:id/activity"
                component={AccountActivity}
              />
              <Route
                exact
                name="carrier-integration-settings"
                path="/users/:id/carrier-settings/billing"
                component={BillingSettings}
              />
              <Route
                exact
                name="carrier-integration-settings"
                path="/users/:id/carrier-settings/quick-pay"
                component={QuickPay}
              />

              <Route
                exact
                name="carrier-integration-settings"
                path="/users/:id/carrier-settings/integrations"
                component={CarrierIntegrationSettings}
              />
              <Route
                exact
                name="carrier-drivers-settings"
                path="/users/:id/carrier-settings/drivers"
                component={DriversSettings}
              />
              <Route
                exact
                name="carrier-notifications-settings"
                path="/users/:id/carrier-settings/notifications"
                component={NotificationsSettings}
              />
              <Route
                exact
                name="carrier-activity-settings"
                path="/users/:id/carrier-settings/activity"
                component={ActivitySettings}
              />
              <Route
                exact
                name="carrier-team-settings"
                path="/users/:id/carrier-settings/team"
                component={CarrierTeam}
              />
            </AuthorizationWrapper>
            <AuthorizationWrapper requiredRoles={[ROLES.ADMIN]}>
              <Route
                exact
                name="rates"
                path="/users/:id/settings/rates"
                component={DrayageFscSettings}
              />
              {!isProd() && SessionStore.isAbleToEditShorthaulFSC() && (
                <Route
                  exact
                  name="shorthaulFscSettings"
                  path="/users/:id/settings/rates/shorthaul/fsc"
                  component={ShorthaulFscSettings}
                />
              )}
              <Route
                exact
                name="drayageRateCard"
                path="/users/:id/rate-card"
                component={RateCard}
              />
            </AuthorizationWrapper>
            <Route
              exact
              name="user"
              path="/consignee/:id"
              component={CustomerDetails}
            />
            <Route
              exact
              name="profile"
              path="/profile"
              component={CustomerDetails}
            />
            <Route
              exact
              name="support-request"
              path="/profile/support-request/:id?"
              component={SupportRequestVerification}
            />
            <Route
              exact
              name="carrier-center"
              path="/carrier-center"
              component={CustomerDetails}
            />
            <Route
              exact
              name="lane-rate-response"
              path="/lane-rate-response/:action"
              component={LaneRateResponse}
            />
            <Route
              key="appointments"
              exact
              name="appointments-module"
              path="/appointments"
              component={AppointmentModule}
            />

            <Switch key="appointment-wizard">
              <Route
                exact
                name="new-appointment"
                path="/appointments/new"
                component={AppointmentDetails}
              />
              <Route
                exact
                name="appointment-details"
                path="/appointments/:id"
                component={AppointmentDetails}
              />
            </Switch>

            <Route
              exact
              name="accessorial-rate-response"
              path="/accessorial-rate-response/:action"
              component={AccessorialRateResponse}
            />
            <Route
              exact
              name="carrier-center-account-requests"
              path="/carrier-center/account-requests/:id?"
              component={AccountAccessVerification}
            />
            <Route
              exact
              name="profile"
              path="/profile/support-request/:id?"
              component={SupportRequestVerification}
            />
            <Route
              exact
              name="carrier-center-support-requests"
              path="/carrier-center/support-request/:id?"
              component={SupportRequestVerification}
            />
            <Route
              exact
              name="developer"
              path="/developer"
              component={DeveloperDetails}
            />
            <Route
              exact
              name="documents"
              path="/documents"
              component={CarrierDocuments}
            />
            <Route
              exact
              name="dashboard"
              path="/carriers/dashboard"
              component={getCarrierDashboardComponent()}
            />
            <Route
              exact
              name="bulk-audit-tool"
              path="/carriers/bulk-audit-tool"
              component={BulkAuditTool}
            />
            <Route
              exact
              name="billing"
              path="/billing"
              component={getBillingComponent()}
            />
            <Route
              exact
              name="notifications"
              path="/notifications"
              component={NotificationsPage}
            />
            <Route
              exact
              name="carrier-payout"
              path="/carrier-payouts/:id"
              component={CarrierPayoutDetails}
            />
            <Route
              exact
              name="driver-tracking"
              path="/driver-tracking"
              component={DriverMap}
            />
            <Route
              exact
              name="driver-list"
              path="/drivers"
              component={DriverList}
            />
            <Route
              exact
              name="manage-driver"
              path="/drivers/:id/manage"
              component={DriverEditForm}
            />
            <Route
              exact
              name="locations"
              path="/locations"
              component={LocationIndex}
            />
            <Route
              exact
              name="location-details"
              path="/locations/:id"
              component={LocationModule}
            />
            <Route
              exact
              name="markets"
              path="/markets"
              component={MarketsIndex}
            />
            <Route exact name="orders" path="/orders" component={OrderList} />
            <Route
              exact
              name="create-order"
              path="/order-booking"
              component={OrderList}
            />
            <Route
              exact
              name="order-details"
              path="/orders/:id"
              component={OrderDetails}
            />
            <Route
              exact
              name="order-details-delivery-order"
              path="/orders/:id/delivery-order"
              render={(props) => <OrderDetails {...props} />}
            />
            <Route
              exact
              name="customer-list"
              path="/customers"
              component={CustomerList}
            />
            <Route
              exact
              name="equipment"
              path="/equipment"
              component={Equipment}
            />
            <PageProvider>
              <Route
                exact
                name="explore-unifiedview"
                path="/unifiedview/favorites"
                component={Reports}
              />
              <Route
                exact
                name="unifiedview-reports"
                path="/unifiedview/my-reports"
                component={Reports}
              />
              <Route
                exact
                name="unifiedview-reports"
                path="/unifiedview/reports"
                component={Reports}
              />
              <Route
                exact
                name="customer-unifiedview"
                path="/unifiedview/dashboard"
                component={UnifiedViewDashboard}
              />
              <Route
                exact
                name="unifiedview-reports"
                path="/unifiedview/groups/:groupId/reports"
                component={Reports}
              />
              <Route
                exact
                name="report-details"
                path="/unifiedview/reports/:reportId"
                component={ExploreUnifiedView}
              />
              <Route
                exact
                name="explore-unifiedview"
                path="/unifiedview/explore"
                component={ExploreUnifiedView}
              />
              <Route
                exact
                name="explore-unifiedview"
                path="/unifiedview/explore/orders"
                component={ExploreUnifiedView}
              />
              <Route
                exact
                name="column-config"
                path="/unifiedview/uv-column-config"
                component={UVColumnConfig}
              />
              <Route
                exact
                name="unifiedview"
                path="/unifiedview"
                component={ExploreUnifiedView}
              />
            </PageProvider>
            <Route
              exact
              name="bulk-shipment-creation"
              path="/bulk"
              component={!isProd() ? BulkShipmentCreation : () => {}}
            />
            <Route
              exact
              name="accessorial-management-center"
              path="/tools/accessorials"
              component={AccessorialManagementCenter}
            />
            <Route exact name="zones" path="/tools/zones" component={Zones} />
            <AuthorizationWrapper>
              {SessionStore.userAllowedToManageReferenceNumbersMasterList() && (
                <Route
                  exact
                  name="reference-numbers"
                  path="/tools/reference-numbers"
                  component={ReferenceNumbersMasterList}
                />
              )}
            </AuthorizationWrapper>
            <Route
              exact
              name="markupconfig"
              path="/marketplace-config/markup"
              component={MarketplaceConfig}
            />
            <Route
              exact
              name="reasoncodes"
              path="/reason_codes"
              component={ReasonCodes}
            />
            <Route
              exact
              name="loadboard"
              path="/loadboard"
              component={LoadBoard}
            />
            <Route
              exact
              name="accessorial-rates-rate-conditions"
              path="/accessorial-rates-v2"
              component={AccessorialRatesRateConditions}
            />
            <Switch key="lane-rates">
              <Route
                exact
                name="lane-rates-rate-conditions"
                path="/lane-rates"
                component={LaneRatesRC}
              />
              <Route
                exact
                name="lane-rates-add"
                path="/lane-rates/add"
                render={(props) => getLaneRatesRateConditionsEdit(props)}
              />
              <Route
                exact
                name="lane-rates-bulk-edit"
                path="/lane-rates/edit"
                render={(props) => getLaneRatesRateConditionsEdit(props)}
              />
              <Route
                exact
                name="lane-rates-bulk-view"
                path="/lane-rates/view"
                component={LaneRatesViewPage}
              />
              <Route
                exact
                name="lane-rates-edit"
                path="/lane-rates/:id/edit"
                render={(props) => getLaneRatesRateConditionsEdit(props)}
              />
              <Route
                exact
                name="lane-rates-view"
                path="/lane-rates/:id"
                component={LaneRatesViewPage}
              />
            </Switch>
            {/* <Route
              exact
              name="lane-rates"
              path="/lane-rates"
              render={(props) => getLaneRates(props)}
            />
            <Route
              exact
              name="lane-rates-add"
              path="/lane-rates/add"
              render={(props) => getLaneRatesEdit(props)}
            />
            <Route
              exact
              name="lane-rates-edit"
              path="/lane-rates/edit"
              render={(props) => getLaneRatesEdit(props)}
            /> */}
            <Route
              exact
              name="accessorial-rates"
              path="/accessorial-rates"
              render={(props) => getAccessorialRates(props)}
            />
            <Route
              exact
              name="offer-planner"
              path="/offer-planner"
              component={(props) => getOfferPlanner(props)}
            />
            <Route exact name="tools" path="/tools" component={ToolsPage} />

            <AuthorizationWrapper requiredRoles={[ROLES.ADMIN]}>
              <Route
                exact
                name="yard-details"
                path="/yards/:id"
                component={YardDetails}
              />

              <Route
                exact
                name="market-rates"
                path="/yards/:id/rates"
                component={MarketRates}
              />

              <Route exact name="yards" path="/yards" component={YardsList} />
            </AuthorizationWrapper>
          </div>
        </div>
      )}
    </div>
  );
}

function authorize(router) {
  let query;
  const currentPath = router.location.pathname;
  const isLoggedIn = SessionStore.isLoggedIn();

  const excludePaths = ['/tracking', '/empty-returns/instructions'];

  query = getQueryString(router.location.search);

  if (!isLoggedIn) {
    if (excludePaths.includes(currentPath)) {
      SessionStore.setRedirectPath(currentPath);
      const redir = `${currentPath}${router.location.search}`;
      return <Redirect to={redir} />;
    }

    if (currentPath !== '/login') {
      if (query.action) {
        SessionStore.setRedirectPath(
          `${currentPath}/${router.location.search}`,
        );
      } else {
        SessionStore.setRedirectPath(currentPath);
      }
    }

    if (currentPath === '/passwordreset' || currentPath === '/createpassword') {
      SessionStore.setRedirectPath('/');
      const redir = `${currentPath}${router.location.search}`;
      return <Redirect to={redir} />;
    }

    if (
      currentPath === '/profile/support-request/' ||
      currentPath === '/carrier-center/support-request/' ||
      currentPath.startsWith('/lane-rate-response/') ||
      currentPath.startsWith('/accessorial-rate-response/')
    ) {
      const redir = `${currentPath}${router.location.search}`;
      SessionStore.setRedirectPath(redir);
      return <Redirect to="/login" />;
    }

    return <Redirect to="/login" />;
  }

  const { _type: type, authorized } = SessionStore.getUser();
  let redirect = SessionStore.getRedirectPath();

  // redirect to welcome page if carrier is not authorized
  const newCarrierPages = [
    '/dashboard',
    '/documents',
    '/carrier-center',
    '/notifications',
    '/billing',
  ];
  if (redirect) {
    query = getQueryString(redirect);
  }

  // Link support requests
  if (type && (type === ROLES.SHIPPER || type === ROLES.CARRIER)) {
    if (redirect && redirect.includes('/support-request/')) {
      return renderPrivate(redirect, currentPath);
    }
    if (redirect && redirect.includes('/lane-rate-response/')) {
      return renderPrivate(redirect, currentPath);
    }
    if (redirect && redirect.includes('/accessorial-rate-response/')) {
      return renderPrivate(redirect, currentPath);
    }
  }

  if (
    type === ROLES.CARRIER &&
    !authorized &&
    !newCarrierPages.includes(currentPath)
  ) {
    //  redirect to my documents if user is not authorized
    redirect = '/documents';
  } else if (
    type === ROLES.CARRIER &&
    authorized &&
    currentPath === '/login' &&
    !checkRedirectPath(query, redirect)
  ) {
    redirect = '/driver-tracking';
  } else if (type === ROLES.SHIPPER && currentPath === '/login') {
    redirect = '/';
  } else if (!redirect && currentPath === '/login') {
    redirect = '/';
  } else if (type === ROLES.SHIPPER && SessionStore.isQuotesOnly()) {
    redirect = '/drayage-quotes';
  } else if (type && type === USER_TYPES.WAREHOUSE) {
    return renderWarehouseRoutes(redirect, currentPath);
  }
  return renderPrivate(redirect, currentPath);
}

function Router(props) {
  const data = useContext(AuthContext);
  const user = SessionStore.getUser();
  const roles = SessionStore.getUserRoles();
  return (
    <AuthContext.Provider
      value={{
        ...data,
        roles,
        _type: user?._type?.toLowerCase() || null,
      }}
    >
      <BrowserRouter onError={routerErrorHandler}>
        <ErrorBoundary>
          <div className={classnames({ 'pt-16': props.nav })}>
            {props.nav && [
              <props.nav key="nav" />,
              <CacheBusting key="cache-busting" />,
            ]}

            <Route name="login" path="/login" component={Login} />
            <Route
              name="passwordreset"
              path="/passwordreset"
              component={PasswordReset}
            />
            <Route
              name="createpassword"
              path="/createpassword"
              component={PasswordReset}
            />
            <Route
              name="tracking"
              path="/tracking"
              component={PublicShipmentTracking}
            />
            <Route
              name="empty-returns-instructions"
              path="/empty-returns/instructions"
              component={EmptyReturnInstructions}
            />
            <Route path="*" render={authorize} />
          </div>
        </ErrorBoundary>
      </BrowserRouter>
    </AuthContext.Provider>
  );
}

Router.propTypes = {
  nav: PropTypes.func.isRequired,
};

export default Router;
